
import { useState, useEffect } from "react";
import { PrebuiltPC } from "@/types/types";
import { useCheckout, CheckoutData } from "@/hooks/use-checkout";
import AddressForm, { AddressData } from "./AddressForm";
import PaymentTabs from "./PaymentTabs/PaymentTabs";
import PaymentButton from "./PaymentButton/PaymentButton";
import SuggestedProducts from "../CustomizePC/OrderSummary/SuggestedProducts";
import { toast } from "sonner";
import ZelleCheckout from "./ZelleCheckout";
import OrderSummary from "./OrderSummary";
import { useBanCheck } from "@/hooks/use-ban-check";
import { PurchaseRestrictedBanner } from "@/components/BanNotice/PurchaseRestrictedBanner";

interface PaymentFormProps {
  product: PrebuiltPC;
  onCancel: () => void;
}

const PaymentForm = ({ product, onCancel }: PaymentFormProps) => {
  const [paymentMethod, setPaymentMethod] = useState<"stripe" | "paypal" | "zelle">("stripe");
  const [addresses, setAddresses] = useState<AddressData | null>(null);
  const [promoCode, setPromoCode] = useState<string>("");
  const [affiliateCode, setAffiliateCode] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState<string | null>(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [cart, setCart] = useState<string[]>([]);
  const [checkoutData, setCheckoutData] = useState<CheckoutData | null>(null);
  const [formValid, setFormValid] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { isBanned, isLoading: banCheckLoading, banInfo } = useBanCheck();
  
  const { startStripeCheckout, startPayPalCheckout } = useCheckout();

  // Generate ban message based on ban info
  const getBanMessage = () => {
    if (!banInfo) return undefined;
    
    let message = "Your account has been restricted from making purchases.";
    
    if (banInfo.reason) {
      message += ` Reason: ${banInfo.reason}.`;
    }
    
    if (banInfo.expires_at) {
      const expiryDate = new Date(banInfo.expires_at);
      message += ` This restriction will be lifted on ${new Date(banInfo.expires_at).toLocaleDateString()}.`;
    }
    
    return message;
  };

  const handleAddToCart = (productId: string) => {
    setCart(prev => [...prev, productId]);
  };

  const handleAddressSubmit = (addressData: AddressData) => {
    setAddresses(addressData);
    setFormValid(true);
    toast.success("Address information saved");
    console.log("Address data submitted:", addressData);
  };

  const handleCheckout = async () => {
    if (!addresses && paymentMethod !== "zelle") {
      toast.error("Please fill in the address information");
      return;
    }

    try {
      setIsProcessing(true);
      
      // Calculate total with credit card fee for Stripe
      const baseAmount = product.price;
      const tax = Math.round(baseAmount * 0.08);
      const shippingFee = 0; // Free shipping
      const creditCardFee = paymentMethod === "stripe" ? Math.round(baseAmount * 0.03) : 0;
      const total = baseAmount + tax + shippingFee + creditCardFee - discountAmount;
      
      // Create checkout data with product details and additional information
      const checkoutPayload: CheckoutData = {
        ...product,
        price: total,
        addresses,
        promoCode,
        accessories: cart,
        affiliateCode,
        couponCode,
        discount: discountAmount
      };
      
      console.log("Starting checkout with payload:", checkoutPayload);
      setCheckoutData(checkoutPayload);
      
      if (paymentMethod === "stripe") {
        await startStripeCheckout(checkoutPayload);
        toast.success("Redirecting to Stripe checkout...");
      } else if (paymentMethod === "paypal") {
        await startPayPalCheckout(checkoutPayload);
        toast.success("Redirecting to PayPal checkout...");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to process payment. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  if (banCheckLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <svg className="animate-spin h-8 w-8 text-gaming-blue" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {isBanned && (
        <PurchaseRestrictedBanner message={getBanMessage()} />
      )}
      
      <PaymentTabs 
        product={product}
        paymentMethod={paymentMethod}
        onPaymentMethodChange={setPaymentMethod}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          {/* Only show address form for stripe/paypal payments */}
          {paymentMethod !== "zelle" ? (
            <>
              <AddressForm onAddressSubmit={handleAddressSubmit} />
              
              <div className="space-y-4 mt-6">
                <SuggestedProducts onAddToCart={handleAddToCart} />

                <PaymentButton 
                  configuration={{
                    ...product,
                    addresses,
                    promoCode,
                    accessories: cart,
                    affiliateCode,
                    couponCode,
                    discount: discountAmount
                  }}
                  paymentMethod={paymentMethod}
                  disabled={!formValid || isBanned}
                  onCheckout={handleCheckout}
                />
              </div>
            </>
          ) : (
            <ZelleCheckout product={product} isDisabled={isBanned} />
          )}
        </div>
        
        <div className="lg:sticky lg:top-6">
          <OrderSummary product={product} paymentMethod={paymentMethod} />
        </div>
      </div>
    </div>
  );
};

export default PaymentForm;
